import { Button } from "@msuite/ui/button";
import { TextInput } from "@msuite/ui/form";
import { ColoredIcon, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import type { TableTypesFormSchema, TicketTypesSchema } from "@msuite/utils/schemas";
import type { Enums } from "@msuite/utils/types";
import { Reorder, useDragControls } from "motion/react";
import { type FieldArrayWithId, useFormContext } from "react-hook-form";
import { TableTypeMenu } from "./table-type-menu";

/** Props Interface */
interface TableTypeCardProps {
	tableType: FieldArrayWithId<TicketTypesSchema, "ticketTypes", "id">;
	index: number;
	isRecentlyAdded: boolean;
	withIconSelect: boolean;
	handleOnAddOption: () => void;
}

export function TableTypeCard({
	tableType,
	index,
	isRecentlyAdded,
	withIconSelect,
	handleOnAddOption,
}: TableTypeCardProps) {
	/** Hooks */
	const { t } = useTranslation();
	const controls = useDragControls();
	const methods = useFormContext<TableTypesFormSchema>();

	/** Functions */
	function handleOnUpdateIcon(iconName: Enums<"iconName">) {
		methods.setValue(`types.${index}.icon`, iconName);
	}

	function handleOnUpdateColor(color: Enums<"color">) {
		tableType.color = color;
		methods.setValue(`types.${index}.color`, color);
	}

	function handleOnToggleDeactivate() {
		const newState = tableType.state === "active" ? "inactive" : "active";
		methods.setValue(`types.${index}.state`, newState);
	}

	function handleOnRemove() {
		if (!isRecentlyAdded) return;
		const tableTypes = methods.getValues("types");
		const newTableTypes = tableTypes.filter((t) => t.id !== tableType.id);
		methods.setValue("types", newTableTypes);
	}

	/** Render */
	return (
		<Reorder.Item
			value={tableType}
			className={cn(tableType.state === "inactive" && "opacity-50!")}
			dragListener={false}
			dragControls={controls}
		>
			<div
				id={`editor-table-type-${tableType.id}`}
				className="h-stack"
				key={index}
				data-cy="table-type-card"
			>
				<div
					onPointerDown={(e) => {
						e.preventDefault();
						controls.start(e);
					}}
					className="text-color cursor-grab active:cursor-grabbing"
				>
					<Icon name="GripVertical" />
				</div>
				<TableTypeMenu
					index={index}
					withIconSelect={withIconSelect}
					onUpdateIcon={handleOnUpdateIcon}
					onUpdateColor={handleOnUpdateColor}
				>
					<div data-cy="table-type-icon">
						{withIconSelect ? (
							<ColoredIcon
								iconName={tableType.icon as Enums<"iconName">}
								color={tableType.color}
								tooltip={tableType.name}
							/>
						) : (
							<div
								className="w-4 aspect-square rounded-full"
								style={{
									backgroundColor: `var(--color-${tableType.color}-500)`,
								}}
							/>
						)}
					</div>
				</TableTypeMenu>
				<TextInput
					label={undefined}
					placeholder={t("general.description")}
					control={methods.control}
					name={`types.${index}.name`}
					isRequired
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleOnAddOption();
							// jump to the next input
							const nextInput = document.querySelector(
								`#editor-table-type-${tableType.id} + .msuite-text-input input`,
							) as HTMLInputElement;
							if (nextInput) {
								nextInput.focus();
							}
						}
					}}
					data-cy="table-type-name-input"
				/>
				<Button
					icon={tableType.state === "active" ? <Icon name="Eye" /> : <Icon name="EyeOff" />}
					variant="ghost"
					onClick={handleOnToggleDeactivate}
					data-cy="table-type-toggle-visibility"
				/>
				<Button
					icon={<Icon name="Trash2" />}
					variant="danger-ghost"
					onClick={handleOnRemove}
					isDisabled={!isRecentlyAdded}
				/>
			</div>
		</Reorder.Item>
	);
}
